import {
  Badge,
  Box,
  Button,
  EmptyState,
  Flex,
  Grid,
  Heading,
  Input,
  Skeleton,
  VStack,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { FiEdit2, FiEye, FiPlus, FiSearch } from "react-icons/fi"

import type { QuestionPublic } from "@/client"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { SubjectSelector } from "@/components/Questions/SubjectSelector"
import { TopicSelector } from "@/components/Questions/TopicSelector"
import { Field } from "@/components/ui/field"
import { InputGroup } from "@/components/ui/input-group"

export interface QuizQuestionBankProps {
  loading: boolean
  availableQuestions: QuestionPublic[]
  searchValue: string
  onSearchChange: (value: string) => void
  selectedSubjects: Array<{ id: string; name: string }>
  onSubjectsChange: (subjects: Array<{ id: string; name: string }>) => void
  selectedTopics: Array<{ id: string; name: string }>
  onTopicsChange: (topics: Array<{ id: string; name: string }>) => void
  isQuestionSelected: (questionId: string) => boolean
  onAddQuestion: (question: QuestionPublic) => void
  onPreviewQuestion: (questionId: string) => void
}

export function QuizQuestionBank({
  loading,
  availableQuestions,
  searchValue,
  onSearchChange,
  selectedSubjects,
  onSubjectsChange,
  selectedTopics,
  onTopicsChange,
  isQuestionSelected,
  onAddQuestion,
  onPreviewQuestion,
}: QuizQuestionBankProps) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6}>
      <Heading size="md" mb={4}>
        Question Bank
      </Heading>
      <VStack gap={4} align="stretch">
        <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={4}>
          <Field label="Search" gridColumn={{ base: "1", lg: "span 1" }}>
            <InputGroup
              startElement={<FiSearch />}
              startElementProps={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Input
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Keywords"
              />
            </InputGroup>
          </Field>
          <Field label="Filter by subjects">
            <SubjectSelector
              selectedSubjects={selectedSubjects}
              onChange={onSubjectsChange}
            />
          </Field>
          <Field label="Filter by topics">
            <TopicSelector
              allowCreate={false}
              selectedTopics={selectedTopics}
              onChange={onTopicsChange}
            />
          </Field>
        </Grid>
        <QuestionBankList
          loading={loading}
          availableQuestions={availableQuestions}
          onAddQuestion={onAddQuestion}
          isQuestionSelected={isQuestionSelected}
          onPreviewQuestion={onPreviewQuestion}
        />
      </VStack>
    </Box>
  )
}

interface QuestionBankListProps {
  loading: boolean
  availableQuestions: QuestionPublic[]
  isQuestionSelected: (questionId: string) => boolean
  onAddQuestion: (question: QuestionPublic) => void
  onPreviewQuestion: (questionId: string) => void
}

function QuestionBankList({
  loading,
  availableQuestions,
  isQuestionSelected,
  onAddQuestion,
  onPreviewQuestion,
}: QuestionBankListProps) {
  if (loading) {
    return (
      <VStack gap={3} align="stretch">
        <Skeleton height="80px" />
        <Skeleton height="80px" />
      </VStack>
    )
  }

  if (availableQuestions.length === 0) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No questions match your filters</EmptyState.Title>
            <EmptyState.Description>
              Adjust search terms or subjects to find questions
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <VStack align="stretch" gap={3}>
      {availableQuestions.map((question) => {
        const alreadyAdded = isQuestionSelected(question.id)
        return (
          <Box
            key={question.id}
            p={4}
            borderWidth="1px"
            borderRadius="md"
            bg={alreadyAdded ? "gray.50" : "white"}
            _dark={{ bg: alreadyAdded ? "gray.800" : "gray.900" }}
          >
            <Flex justify="space-between" align="flex-start" gap={3}>
              <Box flex="1">
                <Heading size="sm" mb={2}>
                  Question
                </Heading>
                <HtmlContent lineClamp={3} content={question.question_text} />
                <Flex gap={2} wrap="wrap" mt={3}>
                  {question.subjects.map((subject) => (
                    <Badge key={subject.id} colorPalette="blue">
                      {subject.name}
                    </Badge>
                  ))}
                  {question.topics.map((topic) => (
                    <Badge key={topic.id} colorPalette="green">
                      {topic.name}
                    </Badge>
                  ))}
                </Flex>
              </Box>
              <VStack align="stretch" gap={2}>
                <Button
                  variant={alreadyAdded ? "outline" : "subtle"}
                  colorPalette="blue"
                  onClick={() => onAddQuestion(question)}
                  disabled={alreadyAdded}
                  gap={2}
                  aria-label={
                    alreadyAdded ? "Question already added" : "Add question"
                  }
                  data-testid={`add-question-${question.id}`}
                >
                  <FiPlus />
                  {alreadyAdded ? "Added" : "Add"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  gap={2}
                  colorPalette="gray"
                  onClick={() => onPreviewQuestion(question.id)}
                >
                  <FiEye />
                  Preview
                </Button>
                <Link
                  to={`/questions/${question.id}/edit`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="ghost" size="sm" gap={2} colorPalette="gray">
                    <FiEdit2 />
                    Edit
                  </Button>
                </Link>
              </VStack>
            </Flex>
          </Box>
        )
      })}
    </VStack>
  )
}
